const ABSOLUTE_URL = /^[a-zA-Z][a-zA-Z\d+.-]*:/;
const BODY_TAGS = new Set([
  '[object ArrayBuffer]',
  '[object Blob]',
  '[object DataView]',
  '[object FormData]',
  '[object ReadableStream]',
  '[object URLSearchParams]',
]);

export class ApiError extends Error {
  constructor(message, options = {}) {
    super(message || 'API request failed');
    this.name = 'ApiError';
    this.status = Number(options.status || 0);
    this.code = String(options.code || (this.status ? 'HTTP_ERROR' : 'NETWORK_ERROR'));
    this.requestId = String(options.requestId || '');
    this.method = String(options.method || 'GET').toUpperCase();
    this.url = String(options.url || '');
    this.details = options.details ?? null;
    this.responseBody = options.responseBody ?? null;
    this.retryable = options.retryable ?? isRetryableStatus(this.status);
    if (options.cause !== undefined) this.cause = options.cause;
  }
}

export function isApiError(value) {
  return value instanceof ApiError;
}

export function createApiClient(options = {}) {
  const fetchImpl = options.fetchImpl || globalThis.fetch;
  if (typeof fetchImpl !== 'function') {
    throw new TypeError('createApiClient requires a fetch implementation');
  }

  const baseUrl = normalizeBaseUrl(options.baseUrl || '');
  const credentials = options.credentials || 'include';
  const getAccessToken = options.getAccessToken || (() => '');
  const setAccessToken = options.setAccessToken || (() => undefined);
  const refreshAccessToken = options.refreshAccessToken;
  const onAuthFailure = options.onAuthFailure || (() => undefined);
  const allowAbsoluteUrls = options.allowAbsoluteUrls === true;
  let refreshPromise = null;

  async function refreshOnce(error) {
    if (typeof refreshAccessToken !== 'function') throw error;
    if (!refreshPromise) {
      refreshPromise = Promise.resolve()
        .then(() => refreshAccessToken({ error }))
        .then(async (result) => {
          const token = accessTokenFromRefreshResult(result);
          if (token) await setAccessToken(token);
          return result;
        })
        .finally(() => {
          refreshPromise = null;
        });
    }
    return refreshPromise;
  }

  async function notifyAuthFailure(error) {
    try {
      await onAuthFailure(error);
    } catch (callbackError) {
      error.authFailureCallbackError = callbackError;
    }
  }

  async function request(path, requestOptions = {}, attempt = 0) {
    const {
      auth = true,
      retryAuth = true,
      responseType = 'auto',
      body,
      headers: inputHeaders,
      ...fetchOptions
    } = requestOptions;
    const method = String(fetchOptions.method || (body === undefined ? 'GET' : 'POST')).toUpperCase();
    const url = buildRequestUrl(baseUrl, path, allowAbsoluteUrls);
    const headers = new Headers(inputHeaders || {});

    if (auth && !headers.has('authorization')) {
      const accessToken = String((await getAccessToken()) || '').trim();
      if (accessToken) headers.set('authorization', `Bearer ${accessToken}`);
    }

    const preparedBody = prepareRequestBody(body, headers);
    let response;
    try {
      response = await fetchImpl(url, {
        ...fetchOptions,
        method,
        credentials: fetchOptions.credentials ?? credentials,
        headers,
        ...(body === undefined ? {} : { body: preparedBody }),
      });
    } catch (cause) {
      throw new ApiError(networkErrorMessage(cause), {
        status: 0,
        code: 'NETWORK_ERROR',
        method,
        url,
        cause,
        retryable: true,
      });
    }

    if (response.ok && responseType === 'response') return response;
    const responseBody = await parseResponseBody(response, responseType === 'response' ? 'auto' : responseType);
    if (response.ok) return responseBody;

    const error = apiErrorFromResponse(response, responseBody, { method, url });
    const shouldTryRefresh = response.status === 401
      && auth
      && retryAuth
      && attempt === 0
      && typeof refreshAccessToken === 'function';

    if (shouldTryRefresh) {
      try {
        await refreshOnce(error);
        return request(path, requestOptions, attempt + 1);
      } catch (refreshError) {
        error.refreshError = refreshError;
        await notifyAuthFailure(error);
        throw error;
      }
    }

    if (response.status === 401 && auth) await notifyAuthFailure(error);
    throw error;
  }

  return {
    request,
    get: (path, requestOptions = {}) => request(path, { ...requestOptions, method: 'GET' }),
    post: (path, body, requestOptions = {}) => request(path, { ...requestOptions, method: 'POST', body }),
    put: (path, body, requestOptions = {}) => request(path, { ...requestOptions, method: 'PUT', body }),
    patch: (path, body, requestOptions = {}) => request(path, { ...requestOptions, method: 'PATCH', body }),
    delete: (path, requestOptions = {}) => request(path, { ...requestOptions, method: 'DELETE' }),
  };
}

function normalizeBaseUrl(value) {
  return String(value || '').trim().replace(/\/+$/, '');
}

function buildRequestUrl(baseUrl, path, allowAbsoluteUrls) {
  const requestPath = String(path || '').trim();
  if (ABSOLUTE_URL.test(requestPath)) {
    if (!allowAbsoluteUrls) {
      throw new TypeError('Absolute request URLs are disabled to prevent credential leakage');
    }
    return requestPath;
  }
  if (!baseUrl) return requestPath || '/';
  if (!requestPath) return baseUrl;
  return `${baseUrl}/${requestPath.replace(/^\/+/, '')}`;
}

function prepareRequestBody(body, headers) {
  if (body === undefined || body === null) return body;
  if (isBodyValue(body)) return body;
  if (!headers.has('content-type')) headers.set('content-type', 'application/json');
  return JSON.stringify(body);
}

function isBodyValue(value) {
  if (typeof value === 'string') return true;
  if (ArrayBuffer.isView(value)) return true;
  return BODY_TAGS.has(Object.prototype.toString.call(value));
}

async function parseResponseBody(response, responseType) {
  if (response.status === 204 || response.status === 205) return null;
  if (responseType === 'blob') return response.blob();
  if (responseType === 'arrayBuffer') return response.arrayBuffer();
  if (responseType === 'text') return response.text();
  if (responseType === 'json') {
    const text = await response.text();
    return text ? JSON.parse(text) : null;
  }

  const text = await response.text();
  if (!text) return null;
  const contentType = String(response.headers.get('content-type') || '').toLowerCase();
  if (contentType.includes('json') || looksLikeJson(text)) {
    try {
      return JSON.parse(text);
    } catch (_error) {
      return text;
    }
  }
  return text;
}

function looksLikeJson(value) {
  const text = String(value || '').trim();
  return (text.startsWith('{') && text.endsWith('}'))
    || (text.startsWith('[') && text.endsWith(']'));
}

function apiErrorFromResponse(response, body, context) {
  const details = body && typeof body === 'object' ? (body.detail ?? body.details ?? null) : null;
  const message = errorMessage(body, response.statusText, response.status);
  const code = errorCode(body, response.status);
  return new ApiError(message, {
    status: response.status,
    code,
    requestId: response.headers.get('x-request-id') || response.headers.get('request-id') || '',
    method: context.method,
    url: context.url,
    details,
    responseBody: body,
  });
}

function errorMessage(body, statusText, status) {
  if (body && typeof body === 'object') {
    if (typeof body.detail === 'string' && body.detail.trim()) return body.detail;
    if (body.detail && typeof body.detail.message === 'string') return body.detail.message;
    if (typeof body.message === 'string' && body.message.trim()) return body.message;
    if (typeof body.error === 'string' && body.error.trim()) return body.error;
  }
  if (typeof body === 'string' && body.trim()) return body.trim();
  return String(statusText || `HTTP ${status || 0}`);
}

function errorCode(body, status) {
  if (body && typeof body === 'object') {
    const code = body.code || body.title || body.error_code || body.detail?.code;
    if (code) return String(code);
  }
  return status ? `HTTP_${status}` : 'NETWORK_ERROR';
}

function accessTokenFromRefreshResult(result) {
  if (typeof result === 'string') return result.trim();
  if (!result || typeof result !== 'object') return '';
  return String(result.accessToken || result.access_token || result.token || '').trim();
}

function isRetryableStatus(status) {
  return status === 0 || status === 408 || status === 425 || status === 429 || status >= 500;
}

function networkErrorMessage(error) {
  const message = String(error?.message || '').trim();
  return message ? `Network request failed: ${message}` : 'Network request failed';
}
